import { usePerms } from '@/hooks/usePerms';
import { useSession } from '@/providers/session-provider';
import { Urls } from '@/lib/urls';
import {
  useQuery,
  type UseQueryResult,
} from '@tanstack/react-query';
import { failedInvoicesCountQueryOptions } from '@/app/dashboard/invoicing/failed-deliveries/utils';

type CountResponse = { count: number; identifier?: string };

type CountQueryFactory = (args: {
  organizationId?: string;
  enabled?: boolean;
  canViewInvoices: boolean;
}) => ReturnType<typeof failedInvoicesCountQueryOptions>;

const customCountQueryRegistry: Record<string, CountQueryFactory> = {
  [Urls.dashboard.invoicing.failedDelivieries]: ({
    organizationId,
    canViewInvoices,
  }) =>
    failedInvoicesCountQueryOptions({
      organizationId,
      countOnly: true,
      enabled: canViewInvoices
    }),
};

export function useCustomCountByPath(
  path: string
): UseQueryResult<CountResponse | undefined, Error> | undefined {
  const {
    data: {
      session: { organizationId },
    },
  } = useSession();
  const { canView: canViewInvoices } = usePerms('invoice');

  const factory = customCountQueryRegistry[path];

  const queryOptions = factory
  ? factory({ organizationId, canViewInvoices })
  : {
      queryKey: ['empty', path],
      queryFn: async () => ({ count: 0 }),
      enabled: false,
    };

  const result = useQuery(queryOptions);
  const isEmptyQuery = queryOptions.queryKey[0] === 'empty';

  return isEmptyQuery ? undefined : result;
}